import { getDevice } from "../device";
import { dmnStorage } from "../dmnStorage";

const storageKey = "featureFlags";

type FeatureFlagsDataStorageType = {
  key: string;
  title: string;
  description?: string;
  isEnabled: boolean;
};

const defaultFeatureFlags: FeatureFlagsDataStorageType[] = [
  {
    key: "showUnsyncedTab",
    title: "Unsynced tab",
    description:
      "Show the unsynced tab when bookings aren't sent to the server straight away",
    isEnabled: true
  },
  {
    key: "showLastSyncDate",
    title: "Show last sync",
    description: "Display the last sync date under the user name",
    isEnabled: false
  },
  {
    key: "showSiteInformation",
    title: "Show site information",
    description: "Display site information",
    isEnabled: false
  },
  // showAICoPilot: Kept in defaults for toggle functionality.
  // Filtered from Feature flags section in SettingsContent.tsx
  // as it has its own dedicated "AI Features" section for Evo-tier users.
  {
    key: "showAICoPilot",
    title: "Space Optimiser Assistant",
    description:
      "Enable AI-powered insights and explanations in Space Optimiser (requires Evo tier)",
    isEnabled: true
  },
  {
    key: "alphaHeader",
    title: "Alpha header",
    description:
      "Preview the redesigned header: venue grouped with context, search in its own slot, utilities collapsed to an overflow menu",
    isEnabled: getDevice() === "mobile"
  },
  {
    key: "showEnglishSpelling",
    title: "English spelling preference",
    description:
      "Let users choose their preferred English spelling (UK/US) in User Preferences",
    isEnabled: false
  },
  {
    key: "alphaBookingActions",
    title: "Alpha: Booking card actions",
    description:
      "Preview Extend (finish-time nudge), late check-in / early check-out prompts, and more prominent covers on the diary bookings card",
    isEnabled: false
  },
  {
    key: "alphaDiaryGridV2",
    title: "Alpha: New diary grid",
    description:
      "Try the redesigned diary grid. For now you can drag bookings to move or resize them; everything else works as it does today. Also turns on live diary updates.",
    isEnabled: false
  },
  {
    key: "alphaDiaryLiveSync",
    title: "Alpha: Diary live sync",
    description:
      "Keep the diary's live connection retrying, reload bookings after it reconnects, and show whether the diary is receiving live updates. Already on when Diary grid v2 is enabled; this flag is for the packaged V1 grid only.",
    isEnabled: false
  },
  {
    key: "alphaRunsheetTable",
    title: "Runsheet table view",
    description:
      "Preview the runsheet as a sortable table with stage actions and column options",
    isEnabled: false
  },
  {
    key: "alphaBookingDetails",
    title: "Booking Details redesign",
    description:
      "Preview the redesigned Booking Details: fields-first Details tab with autosave, left rail, aligned tab set and header actions",
    isEnabled: false
  },
  {
    key: "alphaAtreemoBio",
    title: "Atreemo customer bio",
    description:
      "Match booking guests to their Acteol/Atreemo profile (loyalty, marketing, notes) and open it in place from the Guests tab",
    isEnabled: false
  },
  {
    key: "alphaClassicPalette",
    title: "Classic palette option",
    description:
      "Offer the legacy collins-ui colours as an Appearance option in User Preferences",
    isEnabled: false
  }
];

export const getDefaultFeatureFlags = (): FeatureFlagsDataStorageType[] =>
  defaultFeatureFlags.map((flag) => ({ ...flag }));

// showAICoPilot is switched outside Alpha Features (User Preferences → AI
// features, Evo tier), so it must keep the user's stored value even without
// the early-access capability.
export const FLAGS_WITHOUT_EARLY_ACCESS: readonly string[] = ["showAICoPilot"];

export const addFeatureFlagsStorage = async (
  data: FeatureFlagsDataStorageType[]
) => {
  if (data.length === 0) {
    return;
  }
  return await dmnStorage.add(storageKey, data);
};

export const getFeatureFlagsStorage = async (): Promise<
  FeatureFlagsDataStorageType[]
> => {
  const response: FeatureFlagsDataStorageType[] | undefined =
    await dmnStorage.get(storageKey);

  try {
    const flagsWithDefaults = defaultFeatureFlags.map((defaultFlag) => ({
      ...defaultFlag,
      isEnabled:
        response?.find((storedFlag) => storedFlag.key === defaultFlag.key)
          ?.isEnabled ?? defaultFlag.isEnabled
    }));

    return flagsWithDefaults;
  } catch (e) {
    console.error("Error parsing feature flags from storage:", e);
    return [];
  }
};
